/**
 * DealerButtonView for Poker Reborn
 *
 * Displays the dealer button that moves around the table.
 *
 * @see docs/screens/room/DEALER_BUTTON.md - Button design, position animation
 */

import { Assets, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';
import { IMAGES } from '@/config/assets';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Default button size (diameter) - used before resize() is called */
  defaultSize: 32,

  /** Glow settings */
  glowSize: 8,
};

// =============================================================================
// BREAKPOINT-BASED SCALING (BBS)
// =============================================================================

/**
 * BBS: Dealer button size at specific screen widths.
 * Smooth interpolation between breakpoints.
 *
 * @see docs/screens/room/DEALER_BUTTON.md - Responsive sizing spec
 * @see docs/systems/BBS.md - Unified responsive system
 */
const DEALER_BUTTON_SIZE_BREAKPOINTS: Record<number, number> = {
  1920: 32,  // Large desktop
  1440: 32,  // Desktop
  720: 28,   // Tablet
  375: 24,   // Mobile
};

// =============================================================================
// DEALER BUTTON VIEW
// =============================================================================

export class DealerButtonView extends Container {
  private buttonSprite: Sprite;

  // Effect injection points
  private buttonGlow: Graphics;

  // Current size (updated by resize())
  private currentSize = STYLES.defaultSize;

  constructor() {
    super();

    // Create button glow (behind everything, for effects)
    this.buttonGlow = new Graphics();
    this.buttonGlow.label = 'buttonGlow';
    this.buttonGlow.visible = false;
    this.addChild(this.buttonGlow);

    // Create button sprite from pre-loaded texture
    const texture = Assets.get<Texture>(IMAGES.dealerButton);
    this.buttonSprite = new Sprite(texture);
    this.buttonSprite.anchor.set(0.5);
    this.buttonSprite.label = 'dealerButton';

    // Scale sprite to default size (resize() will update based on screen width)
    this.buttonSprite.width = this.currentSize;
    this.buttonSprite.height = this.currentSize;

    this.addChild(this.buttonSprite);

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.dealer);
  }

  /**
   * Update dealer button size based on screen width (BBS).
   * Call this from TableScreen.resize().
   *
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    this.currentSize = getScaledValue(
      screenWidth,
      DEALER_BUTTON_SIZE_BREAKPOINTS,
      { discrete: false }
    );

    this.buttonSprite.width = this.currentSize;
    this.buttonSprite.height = this.currentSize;
  }

  /**
   * Animate to a new position
   */
  async moveTo(x: number, y: number, duration = 300): Promise<void> {
    // Simple linear animation
    const startX = this.x;
    const startY = this.y;
    const startTime = Date.now();

    return new Promise((resolve) => {
      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out quad
        const eased = 1 - (1 - progress) * (1 - progress);

        this.x = startX + (x - startX) * eased;
        this.y = startY + (y - startY) * eased;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }

  // ===========================================================================
  // PUBLIC EFFECT INJECTION POINT ACCESSORS
  // ===========================================================================

  /** Get button glow graphics (for DealerButtonMoveEffect) */
  getButtonGlow(): Graphics {
    return this.buttonGlow;
  }

  /** Get button sprite */
  getButtonSprite(): Sprite {
    return this.buttonSprite;
  }

  /** Get button position for effect targeting */
  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /**
   * Set button glow effect
   * @param color - Glow color
   * @param intensity - Glow intensity (0-1)
   */
  setButtonGlow(color: number, intensity: number): void {
    const radius = this.currentSize / 2;

    this.buttonGlow.clear();

    if (intensity > 0) {
      this.buttonGlow.circle(0, 0, radius + STYLES.glowSize);
      this.buttonGlow.fill({ color, alpha: intensity * 0.5 });
      this.buttonGlow.visible = true;
    } else {
      this.buttonGlow.visible = false;
    }
  }
}
